import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import {
  TERMS_OF_USE,
  PRIVACY_POLICY,
  RIDER_AGREEMENT,
  DRIVER_AGREEMENT,
  AUDIO_RECORDING_CONSENT,
  CORPORATE_ACCOUNT_TERMS,
  CANCELLATION_REFUND_POLICY,
} from './legalContent';

// ═══════════════ LEGAL DOCUMENTS ═══════════════
export function LegalDocuments() {
  const navigate = useNavigate();
  const { section } = useParams();
  const validIds = ['terms', 'privacy', 'rider', 'driver', 'recording', 'corporate', 'cancellation'];
  const [activeDoc, setActiveDoc] = useState(validIds.includes(section) ? section : 'terms');

  const docs = [
    { id: 'terms', title: 'Terms of Use', content: TERMS_OF_USE },
    { id: 'privacy', title: 'Privacy Policy', content: PRIVACY_POLICY },
    { id: 'rider', title: 'Rider Agreement', content: RIDER_AGREEMENT },
    { id: 'driver', title: 'Driver Agreement', content: DRIVER_AGREEMENT },
    { id: 'recording', title: 'Audio Recording Consent', content: AUDIO_RECORDING_CONSENT },
    { id: 'corporate', title: 'Corporate Account Terms', content: CORPORATE_ACCOUNT_TERMS },
    { id: 'cancellation', title: 'Cancellation & Refund Policy', content: CANCELLATION_REFUND_POLICY },
  ];


  const active = docs.find((d) => d.id === activeDoc);

  return (
    <div className="max-w-[800px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-6 text-brand-dark">Legal Documents</h1>

      <div className="flex gap-2 flex-wrap mb-6">
        {docs.map((d) => (
          <button key={d.id} onClick={() => setActiveDoc(d.id)} className="px-4 py-2 rounded-full cursor-pointer text-[0.85rem]"
            style={{
              border: `1.5px solid ${activeDoc === d.id ? '#2dd4a8' : '#e2e8f0'}`,
              background: activeDoc === d.id ? 'rgba(45,212,168,0.06)' : '#fff',
              fontWeight: activeDoc === d.id ? 600 : 400,
            }}>{d.title}</button>
        ))}
      </div>

      <div className="p-8 rounded-2xl bg-white border border-slate-200">
        <h2 className="font-serif mt-0 mb-4 text-brand-dark">{active?.title}</h2>
        <div className="text-[0.9rem] text-body" style={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
          {active?.content}
        </div>
      </div>

      <BackButtonBottom label="Back" />
    </div>
  );
}

// ═══════════════ ONBOARDING TUTORIAL ═══════════════
export function OnboardingTutorial({ onComplete }) {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const slides = [
    { icon: '🚗', title: 'Welcome to Reservo', desc: 'Your premium pre-booked ride service in NSW. Book rides in advance with upfront pricing.' },
    { icon: '📅', title: 'Book Ahead', desc: 'Schedule rides from 1 hour to 30 days in advance. Choose your preferred service tier and driver.' },
    { icon: '🔒', title: 'Safe & Secure', desc: 'Every trip is protected with a Trip PIN, GPS tracking, and emergency SOS. Your safety is our priority.' },
    { icon: '⭐', title: 'Rate & Review', desc: 'Help improve the community by rating your trips. Your feedback matters.' },
    { icon: '🎉', title: "You're Ready!", desc: "Start by booking your first ride. We'll match you with the perfect driver." },
  ];

  const current = slides[step];
  const isLast = step === slides.length - 1;

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem('reservo_onboarding_complete', 'true');
      onComplete?.();
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('reservo_onboarding_complete', 'true');
    onComplete?.();
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center p-10" style={{ zIndex: 10000, background: '#0a1628' }}>
      {/* Skip button */}
      <button onClick={handleSkip} className="absolute top-5 right-5 px-4 py-2 rounded-full bg-transparent cursor-pointer" style={{ border: '1px solid rgba(255,255,255,0.2)', color: '#94a3b8' }}>Skip</button>

      {/* Content */}
      <div className="text-center max-w-[400px]">
        <div className="text-[4rem] mb-6">{current.icon}</div>
        <h2 className="font-serif text-white mt-0 mb-3 text-[1.8rem]">{current.title}</h2>
        <p className="text-[1rem] mb-10" style={{ color: '#94a3b8', lineHeight: 1.6 }}>{current.desc}</p>
      </div>

      {/* Dots */}
      <div className="flex gap-2 mb-8">
        {slides.map((_, i) => (
          <div key={i} className="h-2 rounded-[4px] transition-all" style={{ width: i === step ? 24 : 8, background: i === step ? '#2dd4a8' : 'rgba(255,255,255,0.2)' }} />
        ))}
      </div>

      {/* Button */}
      <button onClick={handleNext} className="px-12 py-3.5 rounded-full border-0 font-bold text-[1rem] cursor-pointer" style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>{isLast ? 'Get Started' : 'Next'}</button>
    </div>
  );
}